import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, ChevronDown, CheckCircle, X, AlertCircle, FileText, 
  Clock, Truck, Package, Calendar, Tag, Shield, Loader, LayoutGrid, List, Clipboard, ArrowLeft,
  CheckSquare, AlertTriangle, Edit, Eye, ArrowRight } from 'lucide-react';
import { useAuth } from '../../../../../context/AuthContext';
import outboundTruckService from '../services/outboundTruckService';
import { useTimezone } from '../../../../../hooks/useTimezone';
import BusinessUnitName from '../../components/BusinessUnitName';
import TruckTile from '../../components/TruckTile';
import businessUnitService from '../../../../../services/businessUnitService';
import { formatDate } from '../../components/utils/dateUtils';
import LogiwaCompleteWizard from '../components/LogiwaCompleteWizard';

/**
 * OutboundView component for the outbound truck scheduler module
 * Displays trucks that need to be shipped and allows users to progress them through the shipping workflow
 */
const OutboundView = ({ truckData, setTruckData, refreshData, isLoading, socket, isConnected, openTruckEditor }) => {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('board'); // 'board' or 'list'
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);
  
  // Get user's timezone
  const userTimezone = useTimezone();
  
  // Use outbound truck service
  const truckService = outboundTruckService;
  
  // Form state
  const [formData, setFormData] = useState({
    staging_datetime: '',
    conforming: true,
    shipper_notes: '',
  });
  
  // Loading and error states
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Live time tracking
  const [useLiveTime, setUseLiveTime] = useState(true);
  const intervalRef = useRef(null);
  
  // Delivery confirmation dialog
  const [deliveryConfirmOpen, setDeliveryConfirmOpen] = useState(false);

  // Logiwa LTL Complete wizard
  const [logiwaWizardOpen, setLogiwaWizardOpen] = useState(false);
  const [logiwaWizardTruck, setLogiwaWizardTruck] = useState(null);
  
  // Get auth context
  const { currentUser } = useAuth();
  
  // API URL
  const API_URL = process.env.REACT_APP_API_URL || '/api';
  
  // Action labels for this view
  const actionLabels = {
    receiving: 'Shipping',
    putaway: 'Delivery'
  };
  
  // Load business units on component mount
  useEffect(() => {
    const loadBusinessUnits = async () => {
      try {
        const units = await businessUnitService.getAllBusinessUnits();
        setBusinessUnits(units);
      } catch (error) {
        console.error('Error loading business units:', error);
      }
    };
    loadBusinessUnits();
  }, []);
  
  // Handle status transitions
  const handleStatusChange = async (truckId, newStatus) => {
    // LTL Complete (anything not explicitly small-parcel) goes through the Logiwa wizard.
    // Small-parcel and all other transitions keep their existing behavior. This handler
    // is a safety net — the primary entry point is TruckTile -> onLogiwaComplete.
    if (newStatus === 'Completed') {
      const truck = truckData.find((t) => t.id === truckId);
      if (truck && !truck.is_small_parcel) {
        setLogiwaWizardTruck(truck);
        setLogiwaWizardOpen(true);
        return;
      }
    }

    try {
      setLocalLoading(true);
      setError(null);

      let updatedTruck;

      // Use the appropriate method based on the status transition
      switch (newStatus) {
        case 'Staged':
          updatedTruck = await truckService.updateOutboundTruckStatus(truckId, 'Staged', {
            staged_at: new Date().toISOString()
          });
          break;
        case 'Loaded':
          updatedTruck = await truckService.updateOutboundTruckStatus(truckId, 'Loaded', {
            loaded_at: new Date().toISOString()
          });
          break;
        case 'Shipped':
          updatedTruck = await truckService.updateOutboundTruckStatus(truckId, 'Shipped', {
            shipped_at: new Date().toISOString()
          });
          break;
        case 'Completed':
          updatedTruck = await truckService.updateOutboundTruckStatus(truckId, 'Completed', {
            completed_at: new Date().toISOString()
          });
          break;
        case 'Cancelled':
          updatedTruck = await truckService.updateOutboundTruckStatus(truckId, 'Cancelled', {
            cancelled_at: new Date().toISOString()
          });
          break;
        default:
          // Generic status update
          updatedTruck = await truckService.updateOutboundTruckStatus(truckId, newStatus);
      }
      
      // Update the truckData with the updated truck
      if (updatedTruck && updatedTruck.truck) {
        const updatedTruckData = truckData.map(truck => 
          truck.id === truckId ? updatedTruck.truck : truck
        );
        setTruckData(updatedTruckData);
      } else {
        // If no specific truck returned, refresh all data
        refreshData();
      }
      
      setLocalLoading(false);
    } catch (error) {
      console.error('Error changing truck status:', error);
      setError(error.message || 'Error updating truck status');
      setLocalLoading(false);
    }
  };

  // Create stable refs for form values to prevent losing focus
  const formValueRef = useRef({
    shipper_notes: '',
    conforming: true,
    staging_datetime: ''
  });
  
  // Create a ref for our isolated notes field component
  const notesFieldRef = useRef(null);

  // Helper function for robust/fuzzy string matching
  const fuzzyMatch = (text, searchTerm) => {
    if (!text || !searchTerm) return false;
    
    // Convert to lowercase for case-insensitive matching
    const normalizedText = text.toString().toLowerCase();
    const normalizedSearch = searchTerm.toLowerCase();
    
    // Direct substring match
    if (normalizedText.includes(normalizedSearch)) return true;
    
    // Check if all characters of search term appear in order in the text
    let searchIndex = 0;
    for (let i = 0; i < normalizedText.length && searchIndex < normalizedSearch.length; i++) {
      if (normalizedText[i] === normalizedSearch[searchIndex]) {
        searchIndex++;
      }
    }
    return searchIndex === normalizedSearch.length;
  };
  
  // Filter trucks for outbound view
  const outboundTrucks = truckData.filter(truck => {
    // Don't show trucks that are marked as Completed or Cancelled - they go to the History view
    return truck.status !== 'Completed' && truck.status !== 'Cancelled';
  });

  // Filter and sort trucks
  const filteredTrucks = outboundTrucks.filter(truck => {
    // Apply status filter - match DB schema: Scheduled, Staged, Loaded, Shipped
    if (statusFilter !== 'all') {
      if (statusFilter === 'scheduled' && truck.status !== 'Scheduled') return false;
      if (statusFilter === 'staged' && truck.status !== 'Staged') return false;
      if (statusFilter === 'loaded' && truck.status !== 'Loaded') return false;
      if (statusFilter === 'shipped' && truck.status !== 'Shipped') return false;
    }
    
    // Apply search filter — match against the same field set the server's search param
    // covers, so client-side filter and server fallback stay in sync.
    if (searchTerm) {
      const businessUnit = businessUnits.find(unit =>
        String(unit.id) === String(truck.business_unit)
      );
      const fields = [
        truck.carrier,
        truck.customer,
        truck.po,
        truck.order_number,
        truck.destination,
        truck.conf_number,
        truck.scheduler_notes,
        truck.shipper_notes,
        businessUnit && businessUnit.name,
      ];
      if (!fields.some((f) => f && fuzzyMatch(String(f), searchTerm))) {
        return false;
      }
    }

    return true;
  });

  // Sort trucks by scheduled date
  const sortedTrucks = [...filteredTrucks].sort((a, b) => 
    new Date(a.scheduled_at) - new Date(b.scheduled_at)
  );

  // Helper function to group trucks by date within scheduled status
  const groupScheduledByDate = (trucks) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const grouped = {};
    
    trucks.forEach(truck => {
      const scheduledDate = new Date(truck.scheduled_at);
      const dateKey = scheduledDate.toDateString();
      
      // Check if it's today
      const isToday = scheduledDate.toDateString() === today.toDateString();
      const displayKey = isToday ? 'Today' : dateKey;
      
      if (!grouped[displayKey]) {
        grouped[displayKey] = {
          date: scheduledDate,
          trucks: [],
          isToday: isToday
        };
      }
      
      grouped[displayKey].trucks.push(truck);
    });
    
    // Sort the groups by date
    const sortedGroups = Object.entries(grouped).sort((a, b) => {
      // Today always comes first
      if (a[1].isToday) return -1;
      if (b[1].isToday) return 1;
      // Then sort by date
      return a[1].date - b[1].date;
    });
    
    return sortedGroups;
  };

  // Group trucks by status for board view based on DB schema enum values
  // Status enum('Scheduled','Staged','Loaded','Shipped','Completed','Cancelled')
  const groupedTrucks = {
    scheduled: [],    // Trucks with status 'Scheduled'
    stagingReady: [], // Trucks with status 'Staged'
    loading: [],      // Trucks with status 'Loaded'
    completed: []     // Trucks with status 'Shipped'
  };

  // Group trucks by status according to the database enum
  sortedTrucks.forEach(truck => {
    switch (truck.status) {
      case 'Shipped':
        groupedTrucks.completed.push(truck);
        break;
      case 'Loaded':
        groupedTrucks.loading.push(truck);
        break;
      case 'Staged':
        groupedTrucks.stagingReady.push(truck);
        break;
      case 'Scheduled':
        groupedTrucks.scheduled.push(truck);
        break;
      default:
        // Default to scheduled for any other statuses
        groupedTrucks.scheduled.push(truck);
        break;
    }
  });
  
  // Group scheduled trucks by date
  const scheduledByDate = groupScheduledByDate(groupedTrucks.scheduled);

  // Empty state when no trucks match filters
  const EmptyState = () => (
    <div className="bg-slate-750 rounded-lg p-8 text-center h-full flex flex-col items-center justify-center">
      <Truck className="w-16 h-16 text-slate-600 mb-3" />
      <p className="text-slate-400 text-lg mb-2">No trucks match your current filters</p>
      <p className="text-slate-500 mb-4">Try adjusting your search criteria or filters</p>
      {(statusFilter !== 'all' || searchTerm) && (
        <button 
          className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
          onClick={() => {
            setStatusFilter('all');
            setSearchTerm('');
          }}
        >
          Clear Filters
        </button>
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Action bar - search, filters and view toggle */}
      <div className="px-4 py-3 bg-slate-800 rounded-lg mx-4 mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-0 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Search carrier, customer, PO, order #, conf #, destination, notes…"
            className="w-full pl-10 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="bg-slate-700 rounded-lg flex">
            <button 
              className={`p-2 ${viewMode === 'list' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-slate-200'} rounded-l-lg`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <List size={16} />
            </button>
            <button 
              className={`p-2 ${viewMode === 'board' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-slate-200'} rounded-r-lg`}
              onClick={() => setViewMode('board')}
              title="Board View"
            >
              <LayoutGrid size={16} />
            </button>
          </div>
          
          {/* Filter button */}
          <button 
            className="flex items-center gap-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm"
            onClick={() => setFilterOpen(!filterOpen)}
          >
            <Filter size={14} />
            <span className="hidden sm:inline">Filters</span>
            <ChevronDown size={14} className={`transform transition-transform ${filterOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
      
      {/* Main content placeholder - would contain the full implementation */}
      <div className="flex-1 bg-slate-750 rounded-lg mx-4 mb-4 p-4 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 border-t-4 border-r-4 border-green-500 rounded-full animate-spin mb-3"></div>
              <div className="text-slate-400">Loading trucks...</div>
            </div>
          </div>
        ) : sortedTrucks.length === 0 ? (
          <EmptyState />
        ) : (
          <div>
            <h2 className="text-white text-lg mb-4">Outbound Trucks Status Board</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-slate-800 p-3 rounded-lg">
                <h3 className="text-blue-400 text-sm font-semibold mb-2">Scheduled ({groupedTrucks.scheduled.length})</h3>
                <div className="space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto pr-1">
                  {scheduledByDate.length === 0 ? (
                    <div className="text-slate-500 text-sm text-center py-3">No scheduled trucks</div>
                  ) : (
                    scheduledByDate.map(([dateLabel, dateGroup], groupIndex) => (
                      <div key={dateLabel}>
                        {/* Date separator */}
                        <div className={`sticky top-0 z-10 bg-slate-800 ${groupIndex > 0 ? 'mt-3' : ''}`}>
                          <div className="flex items-center py-2">
                            <div className="flex-1 h-px bg-slate-600"></div>
                            <span className={`px-3 text-xs font-medium ${
                              dateGroup.isToday ? 'text-yellow-400' : 'text-slate-400'
                            }`}>
                              {dateGroup.isToday ? 'Today' : formatDate(dateGroup.date, 'eee, MMM d', userTimezone)}
                            </span>
                            <div className="flex-1 h-px bg-slate-600"></div>
                          </div>
                        </div>
                        
                        {/* Trucks for this date */}
                        <div className="space-y-2">
                          {dateGroup.trucks.map(truck => (
                            <TruckTile
                              key={truck.id}
                              truck={truck}
                              borderColor="border-slate-600"
                              statusBgColor="bg-blue-900/30"
                              statusTextColor="text-blue-300"
                              statusBorderColor="border-blue-500/20"
                              openTruckEditor={openTruckEditor}
                              onStatusChange={handleStatusChange}
                              onLogiwaComplete={(t) => { setLogiwaWizardTruck(t); setLogiwaWizardOpen(true); }}
                            />
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="bg-slate-800 p-3 rounded-lg">
                <h3 className="text-green-400 text-sm font-semibold mb-2">Staged ({groupedTrucks.stagingReady.length})</h3>
                <div className="space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto pr-1">
                  {groupedTrucks.stagingReady.map(truck => (
                    <TruckTile
                      key={truck.id}
                      truck={truck}
                      borderColor="border-green-500/20"
                      statusBgColor="bg-green-900/30"
                      statusTextColor="text-green-300"
                      statusBorderColor="border-green-500/20"
                      openTruckEditor={openTruckEditor}
                      onStatusChange={handleStatusChange}
                      onLogiwaComplete={(t) => { setLogiwaWizardTruck(t); setLogiwaWizardOpen(true); }}
                    />
                  ))}
                  {groupedTrucks.stagingReady.length === 0 && (
                    <div className="text-slate-500 text-sm text-center py-3">No trucks in staged status</div>
                  )}
                </div>
              </div>
              <div className="bg-slate-800 p-3 rounded-lg">
                <h3 className="text-purple-400 text-sm font-semibold mb-2">Loaded ({groupedTrucks.loading.length})</h3>
                <div className="space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto pr-1">
                  {groupedTrucks.loading.map(truck => (
                    <TruckTile
                      key={truck.id}
                      truck={truck}
                      borderColor="border-purple-500/20"
                      statusBgColor="bg-purple-900/30"
                      statusTextColor="text-purple-300"
                      statusBorderColor="border-purple-500/20"
                      openTruckEditor={openTruckEditor}
                      onStatusChange={handleStatusChange}
                      onLogiwaComplete={(t) => { setLogiwaWizardTruck(t); setLogiwaWizardOpen(true); }}
                    />
                  ))}
                  {groupedTrucks.loading.length === 0 && (
                    <div className="text-slate-500 text-sm text-center py-3">No trucks in loaded status</div>
                  )}
                </div>
              </div>
              <div className="bg-slate-800 p-3 rounded-lg">
                <h3 className="text-teal-400 text-sm font-semibold mb-2">Shipped ({groupedTrucks.completed.length})</h3>
                <div className="space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto pr-1">
                  {groupedTrucks.completed.map(truck => (
                    <TruckTile
                      key={truck.id}
                      truck={truck}
                      borderColor="border-teal-500/20"
                      statusBgColor="bg-teal-900/30"
                      statusTextColor="text-teal-300"
                      statusBorderColor="border-teal-500/20"
                      openTruckEditor={openTruckEditor}
                      onStatusChange={handleStatusChange}
                      onLogiwaComplete={(t) => { setLogiwaWizardTruck(t); setLogiwaWizardOpen(true); }}
                    />
                  ))}
                  {groupedTrucks.completed.length === 0 && (
                    <div className="text-slate-500 text-sm text-center py-3">No trucks in shipped status</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Logiwa LTL Complete wizard — mounted at the OutboundView root so it overlays everything */}
      <LogiwaCompleteWizard
        isOpen={logiwaWizardOpen}
        truck={logiwaWizardTruck}
        onClose={() => {
          setLogiwaWizardOpen(false);
          setLogiwaWizardTruck(null);
        }}
        onComplete={(resp) => {
          // The server returns the updated truck; patch it into local state so the
          // UI updates without a full refetch. Socket emit also fires server-side.
          if (resp && resp.truck) {
            setTruckData((prev) => prev.map((t) => (t.id === resp.truck.id ? resp.truck : t)));
          } else if (refreshData) {
            refreshData();
          }
        }}
      />
    </div>
  );
};

export default OutboundView;